import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import { trackError } from '@iheartjane/tracking';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { Location } from 'react-router';
import { useLocation, useNavigate } from 'react-router';

import { useCartUser } from '@jane/monolith/components/cart/cartFacade/useCartUser';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import {
  authenticateAppleCode,
  authenticateGoogleCode,
  logInError,
  logInSuccess,
  registerSuccess,
} from '@jane/monolith/customer/redux/customer';
import { selectEmbeddedApp } from '@jane/monolith/customer/redux/embeddedApp';
import { setLoginValue } from '@jane/monolith/customer/redux/login';
import { openModal } from '@jane/monolith/customer/redux/modal';
import { showSuccessNotification } from '@jane/monolith/customer/redux/notifications';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { CustomerSource } from '@jane/monolith/sources/customer';
import type { RegisterSuccessResponse } from '@jane/monolith/sources/registration';
import { RegistrationSource } from '@jane/monolith/sources/registration';
import { ResetPasswordRequestSource } from '@jane/monolith/sources/resetPasswordRequest';
import { validationsToErrors } from '@jane/monolith/utils/form';
import type { JaneAuthenticationErrors } from '@jane/shared-ecomm/components';
import type { Authentication } from '@jane/shared-ecomm/providers';
import { trackCustomerRegistration } from '@jane/shared-ecomm/tracking';
import { setJwt } from '@jane/shared/auth';
import { userQueryKeys } from '@jane/shared/data-access';
import type { AppleAuthResponse } from '@jane/shared/hooks';
import { useAppleSDK, useGoogleSDK } from '@jane/shared/hooks';
import { useEcommApp } from '@jane/shared/providers';

interface RegistrationValues {
  email: string;
  nickname?: string;
  password: string;
  phone: string;
  promotionsOptIn?: boolean;
}

interface UseAuthProps {
  initialStoreId?: number;
  isModal?: boolean;
  onLoginSuccess?: () => void;
}

export const useAuth = ({
  onLoginSuccess,
  isModal = true,
  initialStoreId,
}: UseAuthProps = {}): Authentication => {
  const { appMode } = useEcommApp();
  const location: Location<{ nextPathname?: string }> = useLocation();
  const navigate = useNavigate();
  const runtimeConfig = useRuntimeConfig();
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<JaneAuthenticationErrors | null>(null);
  const appleLoginEnabled = useFlag(FLAGS.c3BoostAppleOauth);
  const { recoverUserCart } = useCartUser();
  const { signIn: googleSignIn } = useGoogleSDK();
  const { signIn: appleSignIn } = useAppleSDK();
  const nextPathname = isModal ? null : location.state?.nextPathname || '/';

  const { partnerId, partnerName } = useCustomerSelector(selectEmbeddedApp);

  // google SSO may be implemented for boost
  // apple has not bee implemented yet
  const showGoogleLogin =
    !runtimeConfig.isFramelessEmbedMode ||
    !!runtimeConfig.ssoCredentials['google_oauth2'];
  const showAppleLogin =
    appleLoginEnabled || !runtimeConfig.isFramelessEmbedMode;

  const dispatch = useCustomerDispatch();

  const handleResetError = () => {
    setErrors(null);
  };

  const handleLoginSuccess = () => {
    queryClient.invalidateQueries({ queryKey: userQueryKeys.janeUser() });
    onLoginSuccess && onLoginSuccess();
  };

  const handleDefaultLogin = async (
    credentials: {
      email: string;
      password: string;
    },
    source: string
  ) => {
    handleResetError();
    return CustomerSource.logIn(credentials).then((response) => {
      const { body, headers } = response;
      const { user, errors } = body;

      dispatch(setLoginValue({ name: 'email', value: credentials.email }));
      if (errors) {
        setErrors({ login: errors.error });

        return dispatch(logInError(errors.error));
      }

      dispatch(
        logInSuccess({
          user,
          headers,
          nextPathname,
          navigate: isModal ? false : navigate,
          source,
        })
      );
      recoverUserCart();
      handleLoginSuccess();
      return undefined;
    });
  };

  const handleLogOut = () => {
    return dispatch(openModal({ name: 'logout' }));
  };

  const handleRegisterSuccess = useCallback(
    (response: RegisterSuccessResponse) => {
      const { headers, body } = response;

      setJwt(headers['Authorization']);
      trackCustomerRegistration({
        customer: body.user,
        partnerChannel: appMode,
        partnerId,
        partnerName,
      });
      dispatch(registerSuccess(body.user));
      handleLoginSuccess();
    },
    [appMode, dispatch, partnerId, partnerName]
  );

  const handleRegister = async (newUser: RegistrationValues) => {
    handleResetError();

    return RegistrationSource.register({
      app_mode_at_registration: appMode,
      store_id_at_registration: initialStoreId,
      ...newUser,
    }).then((response) => {
      const { body } = response;

      if ('errors' in body) {
        dispatch(setLoginValue({ name: 'email', value: newUser.email }));

        setErrors({
          register: Object.values(validationsToErrors(body.errors.validations)),
        });
      }

      const result = handleRegisterSuccess(response as RegisterSuccessResponse);
      if (!('errors' in body)) recoverUserCart();
      return result;
    });
  };

  const handleGoogleLogin = (
    code: google.accounts.oauth2.CodeResponse,
    source: string
  ) => {
    dispatch(
      authenticateGoogleCode({
        code,
        onAuthSuccess: () => {
          recoverUserCart();
          handleLoginSuccess();
        },
        nextPathname,
        navigate: isModal ? false : navigate,
        source,
      })
    );
  };

  const handleGoogleClick = (source: string) => {
    googleSignIn((response) => {
      try {
        handleGoogleLogin(response, source);
      } catch (error) {
        setErrors({ login: 'Google login failed' });
        trackError(new Error('Google login failed'), {
          googleError: error,
        });
      }
    });
  };

  const handleAppleLogin = (
    authMetadata: AppleAuthResponse,
    source: string
  ) => {
    dispatch(
      authenticateAppleCode({
        authMetadata,
        onAuthSuccess: () => {
          recoverUserCart();
          handleLoginSuccess();
        },
        nextPathname,
        navigate: isModal ? false : navigate,
        source,
      })
    );
  };

  const handleAppleClick = async (source: string) => {
    const data = await appleSignIn();

    if (data && 'authorization' in data) {
      handleAppleLogin(data, source);
    } else if (data && 'shouldLogError' in data) {
      setErrors({ login: 'Apple login failed' });
      trackError(new Error('Apple login failed'), {
        appleError: data.error,
      });
    }
  };

  const handleResetPassword = async ({ email }: { email: string }) => {
    handleResetError();
    return ResetPasswordRequestSource.create(email).then((response) => {
      const { success, errors } = response;

      if (errors && errors.error) {
        setErrors({ resetPassword: errors.error });
        return false;
      } else {
        dispatch(showSuccessNotification(success && success.message));
        return true;
      }
    });
  };

  return {
    apple: {
      onClick: handleAppleClick,
      showLogin: showAppleLogin,
    },
    google: {
      onClick: handleGoogleClick,
      showLogin: showGoogleLogin,
    },
    errors,
    login: handleDefaultLogin,
    logOut: handleLogOut,
    register: handleRegister,
    resetError: handleResetError,
    resetPassword: handleResetPassword,
  };
};
